import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, ButtonGroup, Col, Container, Input, InputGroup, InputGroupText, Row, Table } from "reactstrap";
import { FACEBOOK_FORMS_LOADING } from "../../constants/loading.constant";
import { useFacebookFormCore } from "../../cores/facebookForm";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import FormCard from "../component/forms/facebook/FormCard";
import { NoData } from "../component/NoData";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import { useIntl } from "react-intl";
import { useGlobalCore } from "../../cores/globals";
import "./FacebookForms.scss";

type FormViewMode = "grid" | "cards" | "table";
type LeadsFilter = "all" | "with-leads" | "without-leads";

export default function FacebookForms() {
  const intl = useIntl();
  const { isDarkMode } = useGlobalCore();
  const { facebookForms, facebookFormsSyncWarning, getFacebookForms } = useFacebookFormCore();
  const { goToBinding, goToSocial, goToViewFacebookForm } = useRouterCore();
  const { company, companyRole, isAdmin, isReseller, socialSettings } = useMeCore();
  const [viewMode, setViewMode] = useState<FormViewMode>("grid");
  const [search, setSearch] = useState("");
  const [leadsFilter, setLeadsFilter] = useState<LeadsFilter>("all");
  const [sortBy, setSortBy] = useState<"name-asc" | "name-desc" | "leads-desc" | "leads-asc">("leads-desc");

  useEffect(() => {
    if (
      !socialSettings?.facebook || (
        !companyRole?.forms &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller
      )
    ) goToSocial();
  }, [goToSocial, socialSettings, companyRole, isAdmin, isReseller]);

  const reloadForms = useCallback(() => {
    if (!company?.id) return;
    getFacebookForms(company.id);
  }, [company?.id, getFacebookForms]);

  useEffect(() => {
    reloadForms();
  }, [reloadForms]);

  const handleViewFacebookForm = useCallback(async (facebookForm) => {
    goToViewFacebookForm(`${facebookForm.id}`);
  }, [goToViewFacebookForm]);

  const forms = useMemo(
    () =>
      facebookForms.map((form) => ({
        ...form,
        leadsCountNumber: Number(form.leadsCount || 0),
      })),
    [facebookForms]
  );

  const filteredForms = useMemo(() => {
    const query = search.trim().toLowerCase();
    let next = forms.filter((form) => {
      if (!query) return true;
      return (
        form.name.toLowerCase().includes(query) ||
        form.id.toLowerCase().includes(query)
      );
    });

    if (leadsFilter === "with-leads") {
      next = next.filter((form) => form.leadsCountNumber > 0);
    }
    if (leadsFilter === "without-leads") {
      next = next.filter((form) => form.leadsCountNumber === 0);
    }

    next = [...next].sort((a, b) => {
      switch (sortBy) {
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "leads-asc":
          return a.leadsCountNumber - b.leadsCountNumber;
        case "leads-desc":
        default:
          return b.leadsCountNumber - a.leadsCountNumber;
      }
    });
    return next;
  }, [forms, search, leadsFilter, sortBy]);

  const stats = useMemo(() => {
    const total = forms.length;
    const withLeads = forms.filter((f) => f.leadsCountNumber > 0).length;
    const totalLeads = forms.reduce((acc, cur) => acc + cur.leadsCountNumber, 0);
    return { total, withLeads, totalLeads };
  }, [forms]);

  const facebookReady = Boolean(socialSettings?.facebook);
  const adsReady = Boolean(socialSettings?.facebookAds);
  const integrationsReady = facebookReady && adsReady;

  useEffect(() => {
    if (facebookFormsSyncWarning && facebookForms.length > 0) {
      toast.info(
        intl.formatMessage({ id: "app.facebookForm.syncWarning" }),
        { toastId: "facebook-forms-sync-warning", autoClose: 8000 }
      );
    }
  }, [facebookForms.length, facebookFormsSyncWarning, intl]);

  return (
    <Layout>
      <Header2 title={intl.formatMessage({ id: "app.facebookForm.title" })} />
      <Content>
        <UpdateLoadingCard
          loadingId={FACEBOOK_FORMS_LOADING}
          text={intl.formatMessage({ id: "app.facebookForm.updatingForm" })} 
        />
        <Container fluid className={`facebook-forms-ai ${isDarkMode ? "facebook-forms-ai--dark" : ""}`}>
          <div className="facebook-forms-ai__panel mb-3">
            <div className="facebook-forms-ai__panel-top">
              <div className="facebook-forms-ai__stats">
                <span className="facebook-forms-ai__chip">{stats.total} formularios</span>
                <span className="facebook-forms-ai__chip">{stats.totalLeads} leads totales</span>
                <span className="facebook-forms-ai__chip">{stats.withLeads} con respuestas</span>
              </div>
              <ButtonGroup className="facebook-forms-ai__views">
                <Button color="secondary" outline={viewMode !== "grid"} onClick={() => setViewMode("grid")}>
                  Grilla
                </Button>
                <Button color="secondary" outline={viewMode !== "cards"} onClick={() => setViewMode("cards")}>
                  Tarjetas
                </Button>
                <Button color="secondary" outline={viewMode !== "table"} onClick={() => setViewMode("table")}>
                  Tabla
                </Button>
              </ButtonGroup>
            </div>
            {facebookFormsSyncWarning ? (
              <div className="facebook-forms-ai__sync-warning mb-3" role="status">
                {intl.formatMessage({ id: "app.facebookForm.syncWarning" })}
              </div>
            ) : null}
            <Row className="g-2">
              <Col md="5" xs="12">
                <InputGroup>
                  <InputGroupText>
                    <i className="fas fa-search" />
                  </InputGroupText>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre o ID del formulario"
                  />
                </InputGroup>
              </Col>
              <Col md="3" sm="6" xs="12">
                <Input
                  type="select"
                  value={leadsFilter}
                  onChange={(e) => setLeadsFilter(e.target.value as LeadsFilter)}
                >
                  <option value="all">Todos</option>
                  <option value="with-leads">Con leads</option>
                  <option value="without-leads">Sin leads</option>
                </Input>
              </Col>
              <Col md="4" sm="6" xs="12">
                <Input
                  type="select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                >
                  <option value="leads-desc">Leads: mayor a menor</option>
                  <option value="leads-asc">Leads: menor a mayor</option>
                  <option value="name-asc">Nombre: A-Z</option>
                  <option value="name-desc">Nombre: Z-A</option>
                </Input>
              </Col>
            </Row>
          </div>

          {forms.length === 0 ? (
            <div className="facebook-forms-ai__empty">
              <div className="facebook-forms-ai__empty-head">
                <h4>Centro de Formularios con IA</h4>
                <p className="mb-0">
                  Aquí podrás revisar leads, filtrar respuestas y detectar formularios con mejor rendimiento.
                </p>
              </div>

              <div className="facebook-forms-ai__requirements">
                <div className={`facebook-forms-ai__req ${facebookReady ? "is-ok" : "is-pending"}`}>
                  <span className="facebook-forms-ai__dot" />
                  Página de Facebook sincronizada
                </div>
                <div className={`facebook-forms-ai__req ${adsReady ? "is-ok" : "is-pending"}`}>
                  <span className="facebook-forms-ai__dot" />
                  Cuenta de Meta Ads sincronizada
                </div>
              </div>

              <p className="facebook-forms-ai__empty-note">
                {integrationsReady
                  ? intl.formatMessage({ id: "app.facebookForm.emptyIntegrated" })
                  : intl.formatMessage({ id: "app.facebookForm.emptyPendingIntegrations" })}
              </p>

              <div className="d-flex flex-wrap gap-2">
                {integrationsReady ? (
                  <Button color="primary" onClick={reloadForms}>
                    {intl.formatMessage({ id: "app.facebookForm.reloadForms" })}
                  </Button>
                ) : (
                  <Button color="primary" onClick={goToBinding}>
                    Integrar Facebook y Meta Ads
                  </Button>
                )}
                <Button color="secondary" outline onClick={goToBinding}>
                  Abrir Centro de Integraciones
                </Button>
              </div>
            </div>
          ) : filteredForms.length === 0 ? (
            <NoData
              image="generic3"
              text="No hay resultados con los filtros actuales."
              horizontal
            />
          ) : viewMode === "table" ? (
            <div className="facebook-forms-ai__table-wrap">
              <Table responsive hover className="facebook-forms-ai__table mb-0">
                <thead>
                  <tr>
                    <th>Formulario</th>
                    <th>ID</th>
                    <th>Leads</th>
                    <th className="text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredForms.map((facebookForm) => (
                    <tr key={facebookForm.id}>
                      <td>
                        <div className="facebook-forms-ai__table-title">{facebookForm.name}</div>
                      </td>
                      <td>
                        <code className="facebook-forms-ai__table-id">{facebookForm.id}</code>
                      </td>
                      <td>
                        <span className="facebook-forms-ai__table-count">
                          {facebookForm.leadsCountNumber}
                        </span>
                      </td>
                      <td className="text-right">
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          className="facebook-forms-ai__table-action"
                          onClick={() => handleViewFacebookForm(facebookForm)}
                        >
                          Ver leads
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Row>
              {filteredForms.map((facebookForm) => (
                <Col
                  md={viewMode === "grid" ? "4" : "12"}
                  sm={viewMode === "grid" ? "6" : "12"}
                  xs="12"
                  key={facebookForm.id}
                  className="mb-3"
                >
                  <FormCard
                    facebookForm={facebookForm}
                    onView={() => handleViewFacebookForm(facebookForm)}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </Content>
    </Layout>
  );
}
